import { useState } from 'react';
import MicroscopyUpload from '../../../pages/scan/MicroscopyUpload';
import { MICRO_STRUCTURES } from '../../../constants/microStructures';
import { defaultPlateId } from '../quickActions';
import { PlatePicker, stack } from './shared';
import * as S from '../../specimens/styles';

/*
 * A micrograph of the isolate: the scan station's upload (hash here, PUT to
 * the presigned address, POST /microscopy) aimed at one of the isolate's
 * made plates or at its collection / intake record, and typed by what it
 * shows (structure), with the magnification and a note when known. The
 * server reads the scale; "Add image" registers and the drawer closes with
 * a toast, so the row and its culture card are read again.
 */

/** Where the micrograph is filed: the chosen plate, or the origin record. */
export function micrographLink(target, { plateId, item }) {
  if (target === 'plate') return { kind: 'plate', id: plateId };
  if (item?.sample_id) return { kind: 'sample', id: item.sample_id };
  if (item?.external_id) return { kind: 'external', id: item.external_id };
  return null;
}

export default function MicrographForm({ item, plates, extra = {}, t, onDone, onCancel }) {
  const hasOrigin = !!(item.sample_id || item.external_id);
  const [target, setTarget] = useState(plates.length ? 'plate' : hasOrigin ? 'origin' : '');
  const [plateId, setPlateId] = useState(
    extra.plateId && plates.some((p) => p.plate_id === extra.plateId) ? extra.plateId : defaultPlateId(item, plates),
  );
  const [structure, setStructure] = useState(extra.structure || '');
  const [magnification, setMagnification] = useState('');
  const [notes, setNotes] = useState('');

  if (!plates.length && !hasOrigin) return <p style={S.hint} data-testid="quick-micro-none">{t('quick.micrograph.noTarget')}</p>;
  const link = micrographLink(target, { plateId, item });

  return (
    <div style={stack} data-testid="quick-micrograph">
      <p style={{ ...S.hint, margin: 0 }}>{t('quick.micrograph.explain')}</p>
      {plates.length > 0 && hasOrigin && (
        <label style={S.label}>
          {t('quick.micrograph.target')}
          <select value={target} onChange={(e) => setTarget(e.target.value)} style={S.input}
            title={t('tip.quick.microTarget')} data-testid="quick-micro-target">
            <option value="plate">{t('quick.micrograph.targetPlate')}</option>
            <option value="origin">{t('quick.micrograph.targetOrigin')}</option>
          </select>
        </label>
      )}
      {target === 'plate' && <PlatePicker plates={plates} value={plateId} onChange={setPlateId} t={t} />}
      {target === 'origin' && !plates.length && (
        <p style={{ ...S.hint, margin: 0 }}>{t('quick.micrograph.target')}: {t('quick.micrograph.targetOrigin')}</p>
      )}
      <label style={S.label}>
        {t('quick.micrograph.structure')}
        <select value={structure} onChange={(e) => setStructure(e.target.value)} style={S.input}
          title={t('tip.quick.structure')} data-testid="quick-micro-structure">
          <option value="">{t('scan:structure.untyped')}</option>
          {MICRO_STRUCTURES.map((s) => <option key={s} value={s}>{t(`scan:structure.${s}`)}</option>)}
        </select>
      </label>
      <label style={S.label}>
        {t('quick.micrograph.magnification')}
        <input type="number" min="1" step="any" value={magnification} onChange={(e) => setMagnification(e.target.value)}
          style={S.input} title={t('tip.quick.magnification')} data-testid="quick-micro-magnification" />
      </label>
      <label style={S.label}>
        {t('quick.notes')}
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={S.input}
          title={t('tip.quick.notes')} data-testid="quick-micro-notes" />
      </label>
      <MicroscopyUpload link={link} structure={structure} magnification={magnification} notes={notes}
        onDone={() => onDone(t('toast.micrographAdded'))} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" style={S.ghostBtn} onClick={onCancel} title={t('tip.quick.cancel')}>{t('common:cancel')}</button>
      </div>
    </div>
  );
}
